/**
 * 数値フォーマットユーティリティ
 */

/**
 * 数値を日本円形式でフォーマット（¥記号付き）
 * PostgreSQL NUMERIC型は文字列として返される可能性があるためany型で受け取る
 * @param value - フォーマットする数値（string, number, null, undefined）
 * @returns フォーマット済み文字列（例: "¥1,000,000"）※小数点以下は切り捨て
 */
export function formatCurrency(value: any): string {
  if (value === null || value === undefined) {
    return '-';
  }

  // String型の場合はNumber型に変換（PostgreSQL NUMERICは文字列で返される）
  const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);

  if (isNaN(numValue)) {
    return '-';
  }

  // 金額は常に小数点以下を切り捨て
  const flooredValue = Math.floor(numValue);

  return '¥' + flooredValue.toLocaleString('ja-JP', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

/**
 * 数値を日本円形式でフォーマット（円マーク付き）
 * PostgreSQL NUMERIC型は文字列として返される可能性があるためany型で受け取る
 * @param value - フォーマットする数値（string, number, null, undefined）
 * @returns フォーマット済み文字列（例: "¥1,000,000円"）※小数点以下は切り捨て
 */
export function formatCurrencyWithUnit(value: any): string {
  const formatted = formatCurrency(value);
  return formatted === '-' ? '-' : `${formatted}円`;
}

/**
 * 数値を整数形式でフォーマット
 * PostgreSQL NUMERIC型は文字列として返される可能性があるためany型で受け取る
 * @param value - フォーマットする数値（string, number, null, undefined）
 * @returns フォーマット済み文字列（例: "1,000,000"）
 */
export function formatInteger(value: any): string {
  if (value === null || value === undefined) {
    return '-';
  }

  // String型の場合はNumber型に変換
  const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);

  if (isNaN(numValue)) {
    return '-';
  }

  return Math.floor(numValue).toLocaleString('ja-JP', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

/**
 * 数値を小数形式でフォーマット
 * PostgreSQL NUMERIC型は文字列として返される可能性があるためany型で受け取る
 * @param value - フォーマットする数値（string, number, null, undefined）
 * @param decimalPlaces - 小数点以下の桁数（デフォルト: 2）
 * @returns フォーマット済み文字列（例: "12.34"）
 */
export function formatDecimal(
  value: any,
  decimalPlaces: number = 2
): string {
  if (value === null || value === undefined) {
    return '-';
  }

  // String型の場合はNumber型に変換
  const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);

  if (isNaN(numValue)) {
    return '-';
  }

  return numValue.toLocaleString('ja-JP', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
}

/**
 * パーセンテージをフォーマット
 * PostgreSQL NUMERIC型は文字列として返される可能性があるためany型で受け取る
 * @param value - フォーマットする数値（string, number, null, undefined）
 * @param decimalPlaces - 小数点以下の桁数（デフォルト: 2）
 * @returns フォーマット済み文字列（例: "12.34%"）
 */
export function formatPercentage(
  value: any,
  decimalPlaces: number = 2
): string {
  if (value === null || value === undefined) {
    return '-';
  }

  // String型の場合はNumber型に変換
  const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);

  if (isNaN(numValue)) {
    return '-';
  }

  return `${formatDecimal(numValue, decimalPlaces)}%`;
}

/**
 * ランクをフォーマットする（丸囲み文字）
 * @param rank - ランク（A, B, C等）
 * @returns フォーマットされたランク文字列（例: Ⓐ）
 */
export function formatRank(rank: string | null | undefined): string {
  if (!rank) {
    return '-';
  }

  // ランクを丸で囲む（Unicodeの囲み文字を使用）
  const rankCircled: Record<string, string> = {
    'A': 'Ⓐ',
    'B': 'Ⓑ',
    'C': 'Ⓒ',
    'D': 'Ⓓ',
    'E': 'Ⓔ',
    'F': 'Ⓕ',
    'G': 'Ⓖ',
    'H': 'Ⓗ',
    'I': 'Ⓘ',
  };

  return rankCircled[rank.toUpperCase()] || rank;
}
